/**
 * The staff API (`/api/staff/**`): wire types, one function per endpoint, and the query hooks.
 * Kept apart from `api/endpoints.ts` so the staff area is one removable unit. Every write takes a
 * `reason` the server enforces (>= 10 characters, >= 20 for break-glass).
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type UseInfiniteQueryResult,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query'

import { request, requestDownload } from '@/api/client'
import type { Invoice, InvoicePage } from '@/api/types'
import { shouldRetry } from '@/api/queries'

/* --- wire types ------------------------------------------------------------- */

export interface Page<T> {
  items: T[]
  next_cursor: string | null
}

export interface AccountRow {
  id: string
  name: string
  slug: string
  account_state: string
  plan: string
  member_count: number
  zone_count: number
  created_at: string
}

export interface Limits {
  max_zones: number
  max_connections_per_zone: number
  max_channels_per_zone: number
  max_members: number
}

export interface Overrides {
  max_zones: number | null
  max_connections_per_zone: number | null
  max_channels_per_zone: number | null
  max_members: number | null
}

export interface MemberRow {
  user_id: string
  email: string
  name: string | null
  role: string
  user_state: string
  email_verified: boolean
}

export interface StaffZoneRow {
  id: string
  name: string
  zone_state: string
  connected_count: number
  identity_count: number
  pending_message_count: number
  created_at: string
}

/** stripe.md §10: the account's subscription, read-only. `source` is `STRIPE` or `STAFF` (a comp). */
export interface StaffSubscription {
  source: string | null
  status: string
  plan_code: string
  interval: string | null
  extra_seats: number
  current_period_end: string | null
  cancel_at_period_end: boolean
  over_limit_since: string | null
  stripe_customer_id: string | null
  stripe_subscription_id: string | null
}

export interface StaffRecharge {
  enabled: boolean
  threshold_micros: number
  amount_micros: number
  card_saved: boolean
}

export interface AccountDetail {
  id: string
  name: string
  slug: string
  account_state: string
  state_changed_at: string | null
  plan: string
  plan_name: string
  limits: Limits
  overrides: Overrides
  members: MemberRow[]
  zones: StaffZoneRow[]
  balance: { balance_micros: number; currency: string }
  subscription: StaffSubscription | null
  police_recharge: StaffRecharge | null
  pending_deletion_count: number
  created_at: string
  last_activity_at: string | null
}

export interface UserRow {
  id: string
  email: string
  name: string | null
  user_state: string
  email_verified: boolean
  account_count: number
  created_at: string
}

export interface UserDetail {
  id: string
  email: string
  name: string | null
  user_state: string
  state_changed_at: string | null
  email_verified: boolean
  has_connection_token: boolean
  memberships: { account_id: string; account_name: string; account_state: string; role: string }[]
  created_at: string
}

export interface ConnectionRow {
  id: string
  name: string
  type: string
  connection_state: string
  owner_user_id: string | null
  is_identity: boolean
  has_transport: boolean
  paused: boolean
  pending_message_count: number
  last_activity_at: string | null
  created_at: string
}

export interface ZoneDetail {
  id: string
  public_id: string
  name: string
  description: string | null
  account_id: string
  account_name: string
  zone_state: string
  state_changed_at: string | null
  administrator_user_id: string | null
  retention_days: number | null
  poll_idle_seconds: number
  poll_active_seconds: number
  offline_after_seconds: number
  offline_release_days: number
  granted_member_count: number
  pending_message_count: number
  connections: ConnectionRow[]
  created_at: string
}

export interface LedgerRow {
  id: string
  account_id: string
  account_name: string
  kind: string
  amount_micros: number
  balance_after_micros: number
  currency: string
  description: string | null
  zone_id: string | null
  created_at: string
}

export interface DeletionRow {
  id: string
  scope: string
  target_id: string
  account_id: string | null
  account_name: string | null
  requested_by_user_id: string | null
  requested_at: string
  execute_after: string
  state: string
  rows_deleted: number
  attempts: number
  cancellable: boolean
  expeditable: boolean
  last_error: string | null
}

export interface HealthLive {
  now: string
  platform: {
    as_of: string | null
    transports_mcp: number
    transports_agent: number
    identities_online: number
    identities_offline: number
    waits_in_flight: number
    messages_sent_per_minute: number
    messages_delivered_per_minute: number
    queue_depth: number
    queue_top_zones: { zone_id: string; zone_name: string; depth: number }[]
    errors_per_minute: Record<string, number>
    frames_per_minute: Record<string, number>
    client_mix: { client: string; sessions: number }[]
    uploads_bytes_per_minute: number
  }
  replica: { waits_in_flight: number; counters_since_last_rollup: Record<string, number> }
}

export interface BillingHealth {
  seat_sync_failures: {
    account_id: string
    target_quantity: number
    failures: number
    failure_code: string | null
    last_attempt_at: string | null
    next_attempt_at: string | null
  }[]
  recharge_failures: {
    recharge_id: string
    account_id: string
    state: string
    failure_code: string | null
    amount_micros: number
    payment_intent_id: string | null
    at: string
  }[]
  failed_events: {
    event_id: string
    type: string
    event_created_at: string | null
    received_at: string
    attempts: number
  }[]
  /** UNKNOWN recharges a superoperator may settle (T10): `settleable` is false until 23 h have passed. */
  stuck_recharges: StuckRecharge[]
  refund_issues: RefundIssue[]
  pending_refund_requests: number
}

export interface StuckRecharge {
  recharge_id: string
  account_id: string
  state: string
  failure_code: string | null
  amount_micros: number
  charge_method: string | null
  idempotency_key: string | null
  payment_intent_id: string | null
  invoice_id: string | null
  created_at: string
  settleable: boolean
}

export interface RefundIssue {
  refund_id: string
  account_id: string
  state: string
  source: string
  failure_code: string | null
  amount_micros: number
  total_cents: number | null
  invoice_id: string | null
  credit_note_id: string | null
  at: string
}

/** One credit note of a refund. `total_cents` is the amount plus its tax. */
export interface RefundRow {
  id: string
  batch_id: string
  lot_id: string
  request_id: string | null
  source: string
  /** Includes `PENDING_MANUAL`: the credit note exists but the card refund did not go through. */
  state: string
  amount_micros: number
  amount_usd: number
  tax_cents: number | null
  total_cents: number | null
  total_usd: number | null
  out_of_band: boolean
  invoice_id: string | null
  credit_note_id: string | null
  credit_note_url: string | null
  refund_id: string | null
  failure_code: string | null
  created_at: string
  resolved_at: string | null
}

export interface RefundResult {
  batch_id: string
  account_id: string
  amount_micros: number
  amount_usd: number
  pending_manual: boolean
  refunds: RefundRow[]
}

export interface StaffRefundRequest {
  id: string
  account_id: string
  amount_micros: number
  amount_usd: number
  state: string
  reason: string | null
  requested_by: string
  created_at: string
  decided_by: string | null
  decision_reason: string | null
  decided_at: string | null
  batch_id: string | null
}

export interface FundingLot {
  id: string
  /** PAID or GOODWILL; only a paid lot with a remainder is refundable. */
  kind: string
  state: string
  refundable: boolean
  invoice_id: string | null
  invoice_url: string | null
  payment_intent_id: string | null
  pre_tax_cents: number | null
  tax_cents: number | null
  amount_micros: number
  consumed_micros: number
  refunded_micros: number
  refund_pending_micros: number
  remainder_micros: number
  created_at: string
}

/** `GET /api/staff/accounts/{id}/police-funding`. */
export interface PoliceFunding {
  account_id: string
  balance_micros: number
  reserved_micros: number
  lot_debt_micros: number
  refundable_micros: number
  reserved_by_requests_micros: number
  lots: FundingLot[]
  refunds: RefundRow[]
  requests: StaffRefundRequest[]
}

export interface SeriesPoint {
  t: string
  v: number
}

export interface HealthSeries {
  metric: string
  step: string
  from: string
  to: string
  points: SeriesPoint[]
}

export interface AuditEvent {
  audit_id: string
  occurred_at: string
  action: string
  outcome: string
  actor_user_id: string | null
  actor_email: string | null
  actor_tier: string | null
  account_id: string | null
  account_name: string | null
  user_id: string | null
  user_email: string | null
  zone_id: string | null
  zone_name: string | null
  identity_id: string | null
  identity_name: string | null
  reason: string | null
  before: string | null
  after: string | null
  request_id: string | null
  route: string | null
  notified: boolean | null
  /** 2026-10-STAFF-MCP (SM9): which surface the row came from. */
  channel: 'web' | 'mcp'
}

export interface TeamMember {
  user_id: string | null
  email: string
  name: string | null
  tier: string
}

export interface BreakglassGrant {
  grant_id: string
  zone_id: string
  account_id: string
  reason: string
  granted_at: string
  expires_at: string
  ended_at: string | null
}

export interface Impersonation {
  impersonation_id: string
  account_id: string
  reason: string
  started_at: string
  expires_at: string
  ended_at: string | null
}

/** Answer of every account/user/zone/identity/deletion write. */
export interface ActionResult {
  notified?: boolean
  [key: string]: unknown
}

/** `balance-adjustments`: a `refund` kind also carries what it did at Stripe. */
export interface BalanceAdjustmentResult extends ActionResult {
  refund?: RefundResult | null
}

export interface ApproveResult extends ActionResult {
  request: StaffRefundRequest | null
  refund: RefundResult
}

export type BalanceKind = 'credit' | 'refund'

/* --- query strings ---------------------------------------------------------- */

function qs(params: Record<string, string | number | undefined | null>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value !== undefined && value !== null && String(value).trim() !== '') {
      search.set(key, String(value).trim())
    }
  }
  const text = search.toString()
  return text === '' ? '' : `?${text}`
}

function seg(value: string): string {
  return encodeURIComponent(value)
}

/* --- reads ------------------------------------------------------------------ */

export const listAccounts = (
  p: { q?: string; state?: string; cursor?: string },
  signal?: AbortSignal,
) => request<Page<AccountRow>>(`/staff/accounts${qs({ ...p, limit: 50 })}`, signal ? { signal } : {})

export const getAccount = (id: string, signal?: AbortSignal) =>
  request<AccountDetail>(`/staff/accounts/${seg(id)}`, signal ? { signal } : {})

export const listUsers = (p: { q?: string; cursor?: string }, signal?: AbortSignal) =>
  request<Page<UserRow>>(`/staff/users${qs({ ...p, limit: 50 })}`, signal ? { signal } : {})

export const getUser = (id: string, signal?: AbortSignal) =>
  request<UserDetail>(`/staff/users/${seg(id)}`, signal ? { signal } : {})

export const getZone = (id: string, signal?: AbortSignal) =>
  request<ZoneDetail>(`/staff/zones/${seg(id)}`, signal ? { signal } : {})

export interface LedgerFilters {
  account_id?: string
  kind?: string
  from?: string
  to?: string
}

export const listLedger = (f: LedgerFilters & { cursor?: string }, signal?: AbortSignal) =>
  request<Page<LedgerRow>>(`/staff/ledger${qs({ ...f, limit: 50 })}`, signal ? { signal } : {})

export const listDeletions = (p: { state?: string; cursor?: string }, signal?: AbortSignal) =>
  request<Page<DeletionRow>>(`/staff/deletions${qs({ ...p, limit: 50 })}`, signal ? { signal } : {})

export const getHealthLive = (signal?: AbortSignal) =>
  request<HealthLive>('/staff/health/live', signal ? { signal } : {})

export const getBillingHealth = (signal?: AbortSignal) =>
  request<BillingHealth>('/staff/health/billing', signal ? { signal } : {})

export const getHealthSeries = (
  p: { metric: string; from: string; to: string; step: 'minute' | 'hour' },
  signal?: AbortSignal,
) => request<HealthSeries>(`/staff/health/series${qs(p)}`, signal ? { signal } : {})

export interface AuditFilters {
  account_id?: string
  actor_id?: string
  action?: string
}

export const listAudit = (f: AuditFilters & { cursor?: string }, signal?: AbortSignal) =>
  request<Page<AuditEvent>>(`/staff/audit${qs({ ...f, limit: 50 })}`, signal ? { signal } : {})

export const listTeam = (signal?: AbortSignal) =>
  request<TeamMember[]>('/staff/team', signal ? { signal } : {})

export const listBreakglass = (signal?: AbortSignal) =>
  request<BreakglassGrant[]>('/staff/breakglass', signal ? { signal } : {})

export const listImpersonations = (signal?: AbortSignal) =>
  request<Impersonation[]>('/staff/impersonations', signal ? { signal } : {})

/* --- writes ----------------------------------------------------------------- */

const post = <T>(path: string, body: unknown) => request<T>(path, { method: 'POST', body })

export type StaffAction =
  | { kind: 'account-suspend'; id: string }
  | { kind: 'account-reactivate'; id: string }
  | { kind: 'user-suspend'; id: string }
  | { kind: 'user-reactivate'; id: string }
  | { kind: 'user-resend-verification'; id: string }
  | { kind: 'zone-pause'; id: string }
  | { kind: 'zone-unpause'; id: string }
  | { kind: 'identity-release'; zoneId: string; id: string }
  | { kind: 'identity-reissue'; zoneId: string; id: string }
  | { kind: 'deletion-cancel'; id: string }
  | { kind: 'deletion-expedite'; id: string }

/** The path a simple `{reason}` action posts to. */
export function actionPath(action: StaffAction): string {
  switch (action.kind) {
    case 'account-suspend':
      return `/staff/accounts/${seg(action.id)}/suspend`
    case 'account-reactivate':
      return `/staff/accounts/${seg(action.id)}/reactivate`
    case 'user-suspend':
      return `/staff/users/${seg(action.id)}/suspend`
    case 'user-reactivate':
      return `/staff/users/${seg(action.id)}/reactivate`
    case 'user-resend-verification':
      return `/staff/users/${seg(action.id)}/resend-verification`
    case 'zone-pause':
      return `/staff/zones/${seg(action.id)}/pause`
    case 'zone-unpause':
      return `/staff/zones/${seg(action.id)}/unpause`
    case 'identity-release':
      return `/staff/zones/${seg(action.zoneId)}/identities/${seg(action.id)}/release`
    case 'identity-reissue':
      return `/staff/zones/${seg(action.zoneId)}/identities/${seg(action.id)}/reissue`
    case 'deletion-cancel':
      return `/staff/deletions/${seg(action.id)}/cancel`
    case 'deletion-expedite':
      return `/staff/deletions/${seg(action.id)}/expedite`
  }
}

export const runAction = (action: StaffAction, reason: string) =>
  post<ActionResult>(actionPath(action), { reason })

export const changePlan = (accountId: string, plan: string, reason: string) =>
  request<ActionResult>(`/staff/accounts/${seg(accountId)}/plan`, { method: 'PUT', body: { plan, reason } })

export type QuotaFields = {
  max_zones: number | null
  max_connections_per_zone: number | null
  max_channels_per_zone: number | null
  max_members: number | null
}

export const overrideQuotas = (accountId: string, quotas: QuotaFields, reason: string) =>
  request<ActionResult>(`/staff/accounts/${seg(accountId)}/quotas`, {
    method: 'PUT',
    body: { ...quotas, reason },
  })

export const adjustBalance = (accountId: string, kind: BalanceKind, amount: string, reason: string) =>
  post<BalanceAdjustmentResult>(`/staff/accounts/${seg(accountId)}/balance-adjustments`, {
    kind,
    amount: Number(amount),
    reason,
  })

export const listRefundRequests = (state: string, signal?: AbortSignal) =>
  request<StaffRefundRequest[]>(`/staff/refund-requests${qs({ state })}`, signal ? { signal } : {})

export const getPoliceFunding = (accountId: string, signal?: AbortSignal) =>
  request<PoliceFunding>(`/staff/accounts/${seg(accountId)}/police-funding`, signal ? { signal } : {})

/* --- invoices (2026-10-BILLING-IN-APP BI14/BI15) ---------------------------- */

export const listAccountInvoices = (accountId: string, cursor: string | null, signal?: AbortSignal) =>
  request<InvoicePage>(`/staff/accounts/${seg(accountId)}/invoices${qs({ cursor: cursor ?? undefined, limit: 20 })}`, signal ? { signal } : {})

export const downloadAccountInvoicePdf = (accountId: string, invoiceId: string) =>
  requestDownload(`/staff/accounts/${seg(accountId)}/invoices/${seg(invoiceId)}/pdf`, { accept: 'application/pdf' })

export interface ResendInvoiceResult { recipients: number; notified: boolean }
export interface VoidInvoiceResult { invoice: Invoice; notified: boolean }
export interface RetryInvoiceResult { invoice: Invoice; result: 'paid' | 'declined'; decline_code: string | null; notified: boolean }

export const resendInvoice = (accountId: string, invoiceId: string, reason: string) =>
  post<ResendInvoiceResult>(`/staff/accounts/${seg(accountId)}/invoices/${seg(invoiceId)}/resend`, { reason })

export const voidInvoice = (accountId: string, invoiceId: string, reason: string) =>
  post<VoidInvoiceResult>(`/staff/accounts/${seg(accountId)}/invoices/${seg(invoiceId)}/void`, { reason })

export const retryInvoice = (accountId: string, invoiceId: string, reason: string) =>
  post<RetryInvoiceResult>(`/staff/accounts/${seg(accountId)}/invoices/${seg(invoiceId)}/retry`, { reason })

export const useStaffInvoices = (accountId: string | undefined, cursor: string | null): UseQueryResult<InvoicePage, unknown> =>
  useQuery({
    queryKey: ['staff', 'invoices', accountId, cursor],
    queryFn: ({ signal }) => listAccountInvoices(accountId as string, cursor, signal),
    enabled: accountId !== undefined,
    retry: shouldRetry,
  })

export const approveRefundRequest = (id: string, reason: string) =>
  post<ApproveResult>(`/staff/refund-requests/${seg(id)}/approve`, { reason })

export const rejectRefundRequest = (id: string, reason: string) =>
  post<ActionResult>(`/staff/refund-requests/${seg(id)}/reject`, { reason })

export const resolveRefund = (id: string, reason: string) =>
  post<ActionResult>(`/staff/refunds/${seg(id)}/resolve`, { reason })

export const settleRecharge = (
  id: string,
  outcome: 'charged' | 'not_charged',
  stripeId: string,
  reason: string,
) =>
  post<ActionResult>(`/staff/recharges/${seg(id)}/settle`, {
    outcome,
    payment_intent_or_invoice_id: outcome === 'charged' ? stripeId.trim() : null,
    reason,
  })

export const setTeamTier = (userId: string, tier: string, reason: string) =>
  request<TeamMember>(`/staff/team/${seg(userId)}`, { method: 'PUT', body: { tier, reason } })

export const revokeTeamMember = (userId: string, reason: string) =>
  post<undefined>(`/staff/team/${seg(userId)}/revoke`, { reason })

export const startImpersonation = (accountId: string, reason: string) =>
  post<Impersonation>('/staff/impersonations', { account_id: accountId, reason })

export const endImpersonation = (id: string, reason: string) =>
  post<Impersonation>(`/staff/impersonations/${seg(id)}/end`, { reason })

export const grantBreakglass = (zoneId: string, reason: string) =>
  post<BreakglassGrant>('/staff/breakglass', { zone_id: zoneId, reason })

export const endBreakglass = (grantId: string, reason: string) =>
  post<BreakglassGrant>(`/staff/breakglass/${seg(grantId)}/end`, { reason })

/* --- hooks ------------------------------------------------------------------ */

const k = {
  accounts: (q: string, state: string) => ['staff', 'accounts', q, state] as const,
  account: (id: string | undefined) => ['staff', 'account', id] as const,
  users: (q: string) => ['staff', 'users', q] as const,
  user: (id: string | undefined) => ['staff', 'user', id] as const,
  zone: (id: string | undefined) => ['staff', 'zone', id] as const,
  ledger: (f: LedgerFilters) => ['staff', 'ledger', f] as const,
  deletions: (state: string) => ['staff', 'deletions', state] as const,
  live: ['staff', 'health', 'live'] as const,
  series: (metric: string, step: string, from: string, to: string) =>
    ['staff', 'health', 'series', metric, step, from, to] as const,
  audit: (f: AuditFilters) => ['staff', 'audit', f] as const,
  team: ['staff', 'team'] as const,
  breakglass: ['staff', 'breakglass'] as const,
  impersonations: ['staff', 'impersonations'] as const,
}

function useKeyset<T>(
  key: readonly unknown[],
  fetchPage: (cursor: string | undefined, signal: AbortSignal) => Promise<Page<T>>,
  enabled = true,
): UseInfiniteQueryResult<InfiniteData<Page<T>>, unknown> {
  return useInfiniteQuery({
    queryKey: key,
    queryFn: ({ pageParam, signal }) => fetchPage(pageParam, signal),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next_cursor ?? undefined,
    enabled,
    retry: shouldRetry,
  })
}

/** Flattens the loaded pages. */
export function itemsOf<T>(data: InfiniteData<Page<T>> | undefined): T[] {
  return data?.pages.flatMap((page) => page.items) ?? []
}

export const useStaffAccounts = (q: string, state: string, enabled = true) =>
  useKeyset(k.accounts(q, state), (cursor, signal) => listAccounts({ q, state, cursor }, signal), enabled)

export const useStaffAccount = (id: string | undefined): UseQueryResult<AccountDetail, unknown> =>
  useQuery({
    queryKey: k.account(id),
    queryFn: ({ signal }) => getAccount(id as string, signal),
    enabled: id !== undefined,
    retry: shouldRetry,
  })

export const useStaffUsers = (q: string) =>
  useKeyset(k.users(q), (cursor, signal) => listUsers({ q, cursor }, signal))

export const useStaffUser = (id: string | undefined): UseQueryResult<UserDetail, unknown> =>
  useQuery({
    queryKey: k.user(id),
    queryFn: ({ signal }) => getUser(id as string, signal),
    enabled: id !== undefined,
    retry: shouldRetry,
  })

export const useStaffZone = (id: string | undefined): UseQueryResult<ZoneDetail, unknown> =>
  useQuery({
    queryKey: k.zone(id),
    queryFn: ({ signal }) => getZone(id as string, signal),
    enabled: id !== undefined,
    retry: shouldRetry,
  })

export const useStaffLedger = (f: LedgerFilters) =>
  useKeyset(k.ledger(f), (cursor, signal) => listLedger({ ...f, cursor }, signal))

export const useStaffRefundRequests = (state: string): UseQueryResult<StaffRefundRequest[], unknown> =>
  useQuery({
    queryKey: ['staff', 'refund-requests', state],
    queryFn: ({ signal }) => listRefundRequests(state, signal),
    retry: shouldRetry,
  })

export const usePoliceFunding = (id: string | undefined): UseQueryResult<PoliceFunding, unknown> =>
  useQuery({
    queryKey: ['staff', 'police-funding', id],
    queryFn: ({ signal }) => getPoliceFunding(id as string, signal),
    enabled: id !== undefined,
    retry: shouldRetry,
  })

export const useStaffDeletions = (state: string) =>
  useKeyset(k.deletions(state), (cursor, signal) => listDeletions({ state, cursor }, signal))

/** Live tiles refresh every 15 s (S12: 10-30 s). */
export const HEALTH_POLL_MS = 15_000

export const useHealthLive = (): UseQueryResult<HealthLive, unknown> =>
  useQuery({
    queryKey: k.live,
    queryFn: ({ signal }) => getHealthLive(signal),
    refetchInterval: HEALTH_POLL_MS,
    refetchIntervalInBackground: false,
    retry: shouldRetry,
  })

export const useBillingHealth = (): UseQueryResult<BillingHealth, unknown> =>
  useQuery({
    queryKey: ['staff', 'health', 'billing'],
    queryFn: ({ signal }) => getBillingHealth(signal),
    refetchInterval: HEALTH_POLL_MS * 2,
    refetchIntervalInBackground: false,
    retry: shouldRetry,
  })

export const useHealthSeries = (
  metric: string,
  step: 'minute' | 'hour',
  from: string,
  to: string,
): UseQueryResult<HealthSeries, unknown> =>
  useQuery({
    queryKey: k.series(metric, step, from, to),
    queryFn: ({ signal }) => getHealthSeries({ metric, step, from, to }, signal),
    refetchInterval: step === 'minute' ? HEALTH_POLL_MS * 2 : 5 * 60_000,
    retry: shouldRetry,
  })

export const useStaffAudit = (f: AuditFilters, enabled: boolean) =>
  useKeyset(k.audit(f), (cursor, signal) => listAudit({ ...f, cursor }, signal), enabled)

export const useStaffTeam = (enabled: boolean): UseQueryResult<TeamMember[], unknown> =>
  useQuery({
    queryKey: k.team,
    queryFn: ({ signal }) => listTeam(signal),
    enabled,
    retry: shouldRetry,
  })

/** Every staff write ends here: the directory, team and audit views are stale afterwards. */
export function useStaffMutation<TVars, TData>(
  fn: (vars: TVars) => Promise<TData>,
): UseMutationResult<TData, unknown, TVars> {
  const client = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: ['staff'] })
    },
  })
}
